// Unbilled member overage — the settlement step for court time beyond a
// member's included hours.
//
// createMemberBooking writes a memberOverage doc with billed:false for every
// booking that exceeds the allowance, but nothing ever collected it: the ledger
// had no reader anywhere in the app, so accrued court time was invisible unless
// you queried Firestore directly. This page is that reader, plus the action
// that turns accrued hours into an invoice.
//
// Invoices land in customerInvoices (the collection People.js already manages)
// carrying source:'member_overage' and a memberId, so Reports can count them as
// membership income rather than customer income. Marking paid is the same flow
// as any other invoice.
import React, { useEffect, useState, useMemo } from 'react';
import { db } from '../firebase';
import {
  collection, getDocs, getDoc, query, where, doc, writeBatch, addDoc, serverTimestamp,
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Button, Chip, CircularProgress, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Checkbox, Collapse, IconButton,
} from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { localToday } from '../utils/dates';
import { logError } from '../utils/errorLogger';

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

export default function AdminOverage() {
  const [rows, setRows] = useState([]);
  const [names, setNames] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [expanded, setExpanded] = useState({});
  const [selected, setSelected] = useState({});
  const [invoiceFor, setInvoiceFor] = useState(null);
  const [invoiceDate, setInvoiceDate] = useState(localToday());
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  // Firestore rules already stop a non-admin writing here, but without this a
  // member reaching the URL would see their own ledger and an Invoice button
  // that only fails on click.
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) return;
      const snap = await getDoc(doc(db, 'users', u.uid));
      const roles = snap.exists() ? (snap.data().roles || []) : [];
      if (!roles.includes('admin')) navigate('/dashboard');
    });
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'memberOverage'), where('billed', '==', false)));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(r => (Number(r.amount) || 0) > 0)
        .sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
      setRows(list);
      // Resolve member names once rather than per row.
      const ids = [...new Set(list.map(r => r.memberId).filter(Boolean))];
      const found = {};
      await Promise.all(ids.map(async id => {
        try {
          const u = await getDoc(doc(db, 'users', id));
          if (u.exists()) {
            const x = u.data();
            found[id] = `${x.firstName || ''} ${x.lastName || ''}`.trim() || x.email || id;
          }
        } catch { /* name is cosmetic — an unresolved id still bills correctly */ }
      }));
      setNames(found);
    } catch (e) {
      logError('AdminOverage', e.message, e.stack);
      setError('Failed to load overage ledger.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Group by member so a single invoice can cover many bookings.
  const byMember = useMemo(() => {
    const m = {};
    for (const r of rows) {
      const k = r.memberId || '(unknown)';
      (m[k] = m[k] || { memberId: k, rows: [], total: 0, hours: 0 }).rows.push(r);
      m[k].total += Number(r.amount) || 0;
      m[k].hours += (Number(r.regularHours) || 0) + (Number(r.primeHours) || 0) + (Number(r.weekendHours) || 0);
    }
    return Object.values(m).sort((a, b) => b.total - a.total);
  }, [rows]);

  const grandTotal = byMember.reduce((t, m) => t + m.total, 0);

  const selectedRowsFor = (m) => {
    const sel = selected[m.memberId];
    // No explicit selection = the whole member's ledger.
    return !sel || !Object.keys(sel).length ? m.rows : m.rows.filter(r => sel[r.id]);
  };

  const openInvoice = (m) => {
    const picked = selectedRowsFor(m);
    if (!picked.length) { setError('Select at least one line to invoice.'); return; }
    setInvoiceFor({ ...m, picked, amount: picked.reduce((t, r) => t + (Number(r.amount) || 0), 0) });
    setInvoiceDate(localToday());
  };

  const createInvoice = async () => {
    if (!invoiceFor) return;
    setSaving(true); setError('');
    try {
      const { picked, amount, memberId } = invoiceFor;
      const dates = picked.map(r => r.date).filter(Boolean).sort();
      const span = dates.length
        ? (dates[0] === dates[dates.length - 1] ? dates[0] : `${dates[0]} to ${dates[dates.length - 1]}`)
        : 'court time';
      const memberName = names[memberId] || memberId;
      const ref = await addDoc(collection(db, 'customerInvoices'), {
        // memberId + source let Reports attribute this to membership income;
        // customerName keeps it readable in the existing invoice list.
        memberId,
        customerName: memberName,
        source: 'member_overage',
        description: `Court time — ${span} (${picked.length} booking${picked.length === 1 ? '' : 's'})`,
        invoiceDate,
        amount: Math.round(amount * 100) / 100,
        paid: false,
        paidDate: '',
        overageIds: picked.map(r => r.id),
        createdAt: serverTimestamp(),
      });
      // Flip the ledger only after the invoice exists, so a failure here leaves
      // the hours unbilled rather than billed-with-no-invoice.
      const batch = writeBatch(db);
      picked.forEach(r => batch.update(doc(db, 'memberOverage', r.id), {
        billed: true, billedAt: serverTimestamp(), invoiceId: ref.id,
      }));
      await batch.commit();
      setSuccess(`Invoiced ${money(amount)} to ${memberName}. Mark it paid from People → Customers when it settles.`);
      setInvoiceFor(null);
      await load();
      setTimeout(() => setSuccess(''), 6000);
    } catch (e) {
      logError('AdminOverage', e.message, e.stack);
      setError(`Failed to create invoice: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1E3A5F' }}>Unbilled Court Time</Typography>
        <Typography variant="body2" sx={{ color: '#888', mt: 0.5, mb: 3 }}>
          Hours booked beyond a member's included allowance, priced at their tier's overage rate.
          Invoicing moves the amount out of this list; it counts as income when the invoice is marked paid.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
        ) : !byMember.length ? (
          <Paper elevation={1} sx={{ p: 4, textAlign: 'center', borderRadius: 2, border: '1px dashed #ccc' }}>
            <Typography sx={{ color: '#888' }}>Nothing unbilled — every accrued hour has been invoiced.</Typography>
          </Paper>
        ) : (
          <>
            <Paper elevation={2} sx={{ p: 2, borderRadius: 2, mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ color: '#555' }}>
                {byMember.length} member{byMember.length === 1 ? '' : 's'} · {rows.length} booking{rows.length === 1 ? '' : 's'}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#27AE60' }}>{money(grandTotal)}</Typography>
            </Paper>

            <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#1E3A5F' }}>
                    <TableCell sx={{ color: 'white', width: 48 }} />
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Member</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Bookings</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Hours</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Amount</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {byMember.map((m, i) => {
                    const open = !!expanded[m.memberId];
                    const sel = selected[m.memberId] || {};
                    const selCount = Object.values(sel).filter(Boolean).length;
                    return (
                      <React.Fragment key={m.memberId}>
                        <TableRow sx={{ backgroundColor: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                          <TableCell>
                            <IconButton size="small" onClick={() => setExpanded(p => ({ ...p, [m.memberId]: !open }))}>
                              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                            </IconButton>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{names[m.memberId] || m.memberId}</TableCell>
                          <TableCell align="center">{m.rows.length}</TableCell>
                          <TableCell align="center">{m.hours.toFixed(1)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{money(m.total)}</TableCell>
                          <TableCell align="right">
                            <Button size="small" variant="contained" startIcon={<ReceiptIcon />}
                              onClick={() => openInvoice(m)}
                              sx={{ backgroundColor: '#27AE60', '&:hover': { backgroundColor: '#229954' } }}>
                              Invoice{selCount ? ` (${selCount})` : ''}
                            </Button>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={6} sx={{ p: 0, borderBottom: open ? undefined : 'none' }}>
                            <Collapse in={open} unmountOnExit>
                              <Box sx={{ p: 2, backgroundColor: '#f4f6f9' }}>
                                <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1 }}>
                                  Tick individual bookings to invoice only those; leave all unticked to invoice the whole balance.
                                </Typography>
                                <Table size="small">
                                  <TableBody>
                                    {m.rows.map(r => (
                                      <TableRow key={r.id}>
                                        <TableCell sx={{ width: 48 }}>
                                          <Checkbox size="small" checked={!!sel[r.id]}
                                            onChange={e => setSelected(p => ({
                                              ...p, [m.memberId]: { ...(p[m.memberId] || {}), [r.id]: e.target.checked },
                                            }))} />
                                        </TableCell>
                                        <TableCell>{r.date || '—'}</TableCell>
                                        <TableCell>
                                          {[
                                            (Number(r.regularHours) || 0) > 0 && `${r.regularHours}h regular @ ${money(r.regularRate)}`,
                                            (Number(r.primeHours) || 0) > 0 && `${r.primeHours}h prime @ ${money(r.primeRate)}`,
                                            (Number(r.weekendHours) || 0) > 0 && `${r.weekendHours}h weekend @ ${money(r.weekendRate)}`,
                                          ].filter(Boolean).join(' · ') || '—'}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>{money(r.amount)}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        <Dialog open={!!invoiceFor} onClose={() => !saving && setInvoiceFor(null)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 700, color: '#1E3A5F' }}>Invoice court time</DialogTitle>
          <DialogContent>
            {invoiceFor && (
              <Box sx={{ pt: 1 }}>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  <strong>{names[invoiceFor.memberId] || invoiceFor.memberId}</strong> —{' '}
                  {invoiceFor.picked.length} booking{invoiceFor.picked.length === 1 ? '' : 's'} totalling{' '}
                  <Chip size="small" label={money(invoiceFor.amount)} sx={{ fontWeight: 700, backgroundColor: '#E9F7EF', color: '#1E6B3C' }} />
                </Typography>
                <TextField fullWidth type="date" label="Invoice date" value={invoiceDate}
                  onChange={e => setInvoiceDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ mb: 2 }} />
                <Alert severity="info">
                  Creates an unpaid invoice and clears these hours from this list. It becomes income
                  in Reports on the date you mark it paid, under People → Customers.
                </Alert>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setInvoiceFor(null)} disabled={saving}>Cancel</Button>
            <Button variant="contained" onClick={createInvoice} disabled={saving}
              sx={{ backgroundColor: '#27AE60', '&:hover': { backgroundColor: '#229954' } }}>
              {saving ? 'Creating…' : 'Create invoice'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
}
